/**
 * Announces that a person's credential changed on some device, so every view that answers
 * "is this person on that reader" re-reads instead of showing a snapshot from before the
 * sync. That staleness is what once put the SAME four readers on screen as
 * "Removido do dispositivo" in one panel and "Confirmado no dispositivo" in the next.
 *
 * Why this exists rather than calling `listRefreshEvents.emit` directly: a sync reports ONE
 * outcome PER DEVICE, so a resident synced to twelve readers announces twelve times. Folding
 * the burst here means every subscriber — present and future — gets exactly one refetch per
 * sync without having to remember to debounce.
 */

import { listRefreshEvents } from './listRefreshEvents';

/**
 * How long to wait for more reports before announcing. Long enough to cover the gap between
 * per-device reports in a bulk sync, short enough that the UI updates while the operator is
 * still looking at it.
 */
const COALESCE_WINDOW_MS = 150;

/**
 * Hard ceiling on how long a sustained burst may suppress an announcement.
 *
 * The window alone is not enough: a PARALLEL sync (PersonDetailModal) lands all its reports
 * together and folds to one, but a SEQUENTIAL sync (PersonProfile, PermissionsTab,
 * ZonesDevices) reports a device round trip apart — further apart than any sane window — so
 * a plain trailing debounce still fires once per device. Capping the wait turns a 12-device
 * sequential sync into a few refreshes instead of twelve, while still letting the operator
 * watch it progress rather than seeing nothing until the last device.
 */
/**
 * Minimum gap between announcements while a sync keeps reporting.
 *
 * The coalescing window alone cannot bound a SEQUENTIAL sync (PersonProfile,
 * PermissionsTab, ZonesDevices): its reports arrive a device round trip apart — further
 * apart than any sane window — so each one settles on its own and a plain trailing debounce
 * fires once per device. Throttling instead turns a 12-device sequential sync into a
 * handful of refreshes while still letting the operator watch it progress.
 */
const MIN_INTERVAL_MS = 1000;

let pending: ReturnType<typeof setTimeout> | undefined;
let lastAnnouncedAt = 0;

function fire(): void {
  pending = undefined;
  lastAnnouncedAt = Date.now();
  // listRefreshEvents.emit already isolates each listener's errors, so one broken
  // subscriber cannot stop the others from refreshing.
  listRefreshEvents.emit('device-credentials');
}

export function announceDeviceCredentialChange(): void {
  // Settle fast when nothing announced recently (a parallel sync folds to one); otherwise
  // wait out the remainder of the interval, so a long sync cannot announce per device.
  const sinceLast = Date.now() - lastAnnouncedAt;
  const wait = Math.max(COALESCE_WINDOW_MS, MIN_INTERVAL_MS - sinceLast);

  if (pending) clearTimeout(pending);
  pending = setTimeout(fire, wait);
}

/** Test seam: drops any pending announcement so suites cannot leak timers into each other. */
export function resetDeviceCredentialAnnouncements(): void {
  if (pending) clearTimeout(pending);
  pending = undefined;
  lastAnnouncedAt = 0;
}
